import { jsx as _jsx } from "preact/jsx-runtime";
import { createContext } from 'preact';
import { useContext, useState, useCallback, useMemo } from 'preact/hooks';
const ToastContext = createContext(undefined);
export function useToast() {
    const context = useContext(ToastContext);
    if (context === undefined) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const generateId = () => Math.random().toString(36).substring(2) + Date.now().toString(36);
    const addToast = useCallback((toast) => {
        const id = generateId();
        const newToast = { ...toast, id };
        setToasts(prev => [...prev, newToast]);
    }, []);
    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(toast => toast.id !== id));
    }, []);
    const showSuccess = useCallback((title, message, duration) => {
        addToast({ type: 'success', title, message, duration });
    }, [addToast]);
    const showError = useCallback((title, message, duration) => {
        addToast({ type: 'error', title, message, duration: duration || 7000 }); // Errors stay longer
    }, [addToast]);
    const showErrorUnless422 = useCallback((title, response, fallback) => {
        if (response.status === 422)
            return;
        showError(title, response.error || fallback || 'Something went wrong.');
    }, [showError]);
    const showWarning = useCallback((title, message, duration) => {
        addToast({ type: 'warning', title, message, duration });
    }, [addToast]);
    const showInfo = useCallback((title, message, duration) => {
        addToast({ type: 'info', title, message, duration });
    }, [addToast]);
    const showValidationErrors = useCallback((errors, duration) => {
        errors.forEach(error => {
            showError(error.Title, error.Message, duration);
        });
    }, [showError]);
    const clearAll = useCallback(() => {
        setToasts([]);
    }, []);
    const value = useMemo(() => ({
        toasts,
        addToast,
        removeToast,
        showSuccess,
        showError,
        showErrorUnless422,
        showWarning,
        showInfo,
        showValidationErrors,
        clearAll
    }), [toasts, addToast, removeToast, showSuccess, showError, showErrorUnless422, showWarning, showInfo, showValidationErrors, clearAll]);
    return (_jsx(ToastContext.Provider, { value: value, children: children }));
}
